import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Image,
  Stack,
  StackProps,
  Text,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import Logo from "assets/camino_logo_transparent.svg";
import apiClient from "services/ApiClient";
import { isNetworkingError } from "services/ApiClient/networkingError";
import { useState, useMemo, useEffect } from "react";
import { API_ROUTES } from "definitions/constants/routeConstants";
import SignInOptionsForm from "components/Forms/SignInOptionsForm";
import useModal from "hooks/useModal";
import Alert from "../Alert/Alert";

const TOP_CALL_TO_ACTION = "Have an account?";
const TOP_CALL_TO_ACTION2 = "Log in here";

const SUCCESS_ALERT_STATE = {
  title: "Check Your Email",
  body: <Text>We&apos;ve sent you a link to sign in. If you don&apos;t see it, check your spam folder.</Text>,
  buttonTitle: "Got it!",
};

// Map error codes from OAuth/SSO redirects to user-friendly messages
const ERROR_MESSAGES: Record<string, { title: string; body: React.ReactNode }> = {
  sso_required: {
    title: "SSO Login Required",
    body: (
      <>
        <Text mb="2">Your organization requires you to log in using Single Sign-On (SSO).</Text>
        <Text>Please contact your IT administrator or reach out to support@joincamino.com for assistance.</Text>
      </>
    ),
  },
  pre_start_auth_not_allowed: {
    title: "Login Not Available",
    body: (
      <>
        <Text mb="2">Personal email login is only available before your start date.</Text>
        <Text>Please use your work email or contact your HR team for assistance.</Text>
      </>
    ),
  },
  company_not_found: {
    title: "Company Not Found",
    body: (
      <>
        <Text mb="2">We couldn&apos;t find a Camino company associated with your account.</Text>
        <Text>If you think this is an error, please reach out to support@joincamino.com.</Text>
      </>
    ),
  },
  user_not_found: {
    title: "Account Not Found",
    body: (
      <>
        <Text mb="2">We couldn&apos;t find an account associated with your email.</Text>
        <Text>Please contact your HR team or reach out to support@joincamino.com for help.</Text>
      </>
    ),
  },
  saml_error: {
    title: "SSO Login Failed",
    body: (
      <>
        <Text mb="2">There was a problem with your SSO login.</Text>
        <Text>Please try again or contact your IT administrator for assistance.</Text>
      </>
    ),
  },
  saml_validation_failed: {
    title: "SSO Validation Failed",
    body: (
      <>
        <Text mb="2">The SSO response could not be validated.</Text>
        <Text>This may be a configuration issue. Please contact your IT administrator.</Text>
      </>
    ),
  },
  oidc_error: {
    title: "SSO Login Failed",
    body: (
      <>
        <Text mb="2">There was a problem with your SSO login.</Text>
        <Text>Please try again or contact your IT administrator for assistance.</Text>
      </>
    ),
  },
  invalid_token: {
    title: "Invalid Login Token",
    body: (
      <>
        <Text mb="2">The login token is invalid or has expired.</Text>
        <Text>Please try logging in again.</Text>
      </>
    ),
  },
  provider_not_found: {
    title: "SSO Not Configured",
    body: (
      <>
        <Text mb="2">SSO is not configured for your organization.</Text>
        <Text>Please contact your IT administrator or use an alternative login method.</Text>
      </>
    ),
  },
  authentication_failed: {
    title: "Authentication Failed",
    body: (
      <>
        <Text mb="2">We couldn&apos;t complete your login.</Text>
        <Text>Please try again or contact support@joincamino.com if the problem persists.</Text>
      </>
    ),
  },
  oauth_error: {
    title: "Google Login Failed",
    body: (
      <>
        <Text mb="2">There was a problem signing in with Google.</Text>
        <Text>Please try again or use an alternative login method.</Text>
      </>
    ),
  },
};

interface AuthenticationOptions {
  hasSSO: boolean;
  ssoEnforced: boolean;
  ssoUrl?: string;
  ssoProviderName?: string;
}

// Parse error from URL query params on initial load (OAuth/SSO redirects)
function getInitialErrorFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get("error");
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    urlParams.delete("error");
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
    return ERROR_MESSAGES[errorCode];
  }
  return null;
}

export default function SignInForm(props: StackProps) {
  const [initialError] = useState(getInitialErrorFromUrl);

  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure({
    defaultIsOpen: !!initialError,
  });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [email, setEmail] = useState<string>("");
  const [alertState, setAlertState] = useState(() =>
    initialError
      ? { title: initialError.title, body: initialError.body, buttonTitle: "OK" }
      : SUCCESS_ALERT_STATE,
  );
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [authOptions, setAuthOptions] = useState<AuthenticationOptions | null>(null);
  const [bypassSSO, setBypassSSO] = useState(false);

  const {
    open: openSignInModal,
    close: closeSignInModal,
    Modal: SignInModal,
  } = useModal({
    headerTitle: "Sign in",
    size: "md",
    isCentered: true,
  });

  // Check for iss query parameter on mount (IdP-initiated OIDC flow)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const issuer = urlParams.get("iss");

    if (issuer) {
      // Automatically fetch authentication options for the IdP (no email needed)
      const fetchAuthOptionsForIdP = async () => {
        setIsCheckingAuth(true);
        try {
          const response = await apiClient.post<AuthenticationOptions>(API_ROUTES.sessions.authenticationOptions, {
            iss: issuer,
          });
          setAuthOptions(response);
          setEmail(""); // Clear email since we're using IdP-initiated flow
          openSignInModal();

          // Clean up URL by removing iss parameter
          urlParams.delete("iss");
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        } catch (error) {
          const errorMessage =
            isNetworkingError(error) && error.errors.length > 0
              ? error.errors[0]
              : "There was a problem with SSO login. Please try again or contact your IT administrator.";

          setAlertState({
            title: "SSO Login Error",
            body: <Text>{errorMessage}</Text>,
            buttonTitle: "OK",
          });
          onAlertOpen();

          // Clean up URL on error
          urlParams.delete("iss");
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        } finally {
          setIsCheckingAuth(false);
        }
      };

      fetchAuthOptionsForIdP();
    }
  }, [onAlertOpen, openSignInModal]);

  const checkAuthenticationOptions = async () => {
    if (!email.trim()) return;

    setIsCheckingAuth(true);
    try {
      const response = await apiClient.post<AuthenticationOptions>(API_ROUTES.sessions.authenticationOptions, {
        email: email.trim(),
      });
      setAuthOptions(response);
      openSignInModal();
    } catch (error) {
      const errorMessage =
        isNetworkingError(error) && error.errors.length > 0
          ? error.errors[0]
          : "There was a problem checking your authentication options. Please try again.";

      setAlertState({
        title: "Error",
        body: <Text>{errorMessage}</Text>,
        buttonTitle: "OK",
      });
      onAlertOpen();
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleNewHireBailout = () => {
    setBypassSSO(true);
    setAuthOptions({
      hasSSO: false,
      ssoEnforced: false,
    });
    openSignInModal();
  };

  const handleMagicLinkSent = () => {
    setAlertState(SUCCESS_ALERT_STATE);
    onAlertOpen();
  };

  const handleAlertClose = useMemo(
    () => () => {
      onAlertClose();
    },
    [onAlertClose],
  );

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && email.trim()) {
      checkAuthenticationOptions();
    }
  };

  return (
    <>
      <Stack spacing="6" {...props}>
        <Stack spacing="4">
          {isMobile && <Image mx={{ base: 6 }} maxWidth="160px" src={Logo} />}
          <Stack spacing={{ base: "2", md: "3" }} textAlign="center">
            <Heading size={{ base: "xs", md: "sm" }}>{TOP_CALL_TO_ACTION}</Heading>
            <Heading size={{ base: "xs", md: "sm" }}>{TOP_CALL_TO_ACTION2}</Heading>
          </Stack>
        </Stack>

        <Stack spacing="4">
          <FormControl
            position="relative"
            sx={{
              "&:focus-within .sign-in-label": {
                transform: "scale(0.75) translateY(-10px)",
              },
              "input:not(:placeholder-shown) + .sign-in-label": {
                transform: "scale(0.75) translateY(-10px)",
              },
            }}
          >
            <Input
              id="email"
              type="email"
              placeholder=" "
              value={email}
              onChange={(event) => setEmail(event.currentTarget.value)}
              onKeyPress={handleKeyPress}
              height="3rem"
              paddingTop="18px"
            />
            <FormLabel
              className="sign-in-label"
              htmlFor="email"
              position="absolute"
              top="14px"
              left="12px"
              color="gray.400"
              fontSize="md"
              pointerEvents="none"
              transition="all 0.2s"
              transformOrigin="left top"
              zIndex={2}
              m={0}
              pr={2}
            >
              Enter your work email
            </FormLabel>
          </FormControl>

          <Button
            type="submit"
            variant="primary"
            isDisabled={!email.trim()}
            isLoading={isCheckingAuth}
            onClick={checkAuthenticationOptions}
          >
            Continue
          </Button>

          <Stack spacing="2" textAlign="center">
            <Text fontSize="sm" color="gray.600">
              Are you a new hire and haven&apos;t yet started?
            </Text>
            <Button variant="link" fontSize="sm" color="brand.500" fontWeight="medium" onClick={handleNewHireBailout}>
              Click here to sign in
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Alert {...alertState} isOpen={isAlertOpen} onClose={handleAlertClose} />

      <SignInModal>
        {authOptions && (
          <>
            {email && (
              <Text fontSize="sm" fontWeight="normal" color="gray.600" mb={4}>
                {email}
              </Text>
            )}
            <SignInOptionsForm
              email={email.trim()}
              ssoUrl={bypassSSO ? undefined : authOptions.ssoUrl}
              ssoProviderName={bypassSSO ? undefined : authOptions.ssoProviderName}
              ssoEnforced={bypassSSO ? false : authOptions.ssoEnforced}
              onMagicLinkSent={handleMagicLinkSent}
              onClose={closeSignInModal}
            />
          </>
        )}
      </SignInModal>
    </>
  );
}
